import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Modal,
  Stack,
  Text,
  Tooltip,
  Anchor,
} from "@mantine/core";
import { IconBell, IconCheck, IconMenu2, IconCircleCheck, IconX, IconChevronDown } from "@tabler/icons";
import { useEffect, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { showNotification } from "@mantine/notifications";
import { useAuth0 } from "@auth0/auth0-react";
import posthog from "posthog-js";
import useEvidenceActions from "../../../../../hooks/useEvidenceActions";
import { useIdeaPage } from "../../../../../hooks/ideaPage/useIdeaPage";
import { useParams } from "react-router-dom";
import { useEvidenceDrawer } from "../../../../../hooks/useEvidenceDrawer";
import { isBagelAdmin } from "../../../../../utils";
import { assignRequestToNewIdea, ideaValidation, labelMatch, updateValidation } from "../../../../../Api";
import { AiMatchReAssignment } from "./AiMatchReAssignment";

import AssignmentVerificationModal from "../actions/MatchActions/AssignmentVerificationModal";
import AiCommentsMenu from "../Comment/AiCommentsMenu";
import BagelUsersActions from "../actions/MatchActions/BagelUsersActions";
import AssignActionsMenu from "../actions/AssignActionsMenu";
import AiAssignmentVerificationModal from "./AiAssignmentVerificationModal";
import {useKnowledgeData} from "../../../../../hooks/useKnowledgeData";
import {useFields} from "../../../../../hooks/useFields";
import {useUserPermissions} from "../../../../../hooks/useUserPermissions";
import { CLASS_NAMES } from "../constants";
import { removeEvidenceFromSuggestedInfiniteCache, refetchTabEvidenceTotals } from "../../../../../hooks/ideaPage/suggestedInfiniteCacheHelpers";
import { useCurrentUser } from "../../../../../hooks/useCurrentUser";


const AiMatchActions = ({ 
  setShowHoverCard,
  hoverCard,
  navToPerspective, 
  componentId, 
  chosenEvidence, 
  validation,
  setideaValidationData,
  setMaturity,
  handlebannerText,
  setLoading,
  setValidationCount,
  setIsValidationDone,
  componentType = 'idea',
  getUnreadNotificationsCountByRequest,
}: { 
  setShowHoverCard?: (show: boolean) => void,
  navToPerspective: any, 
  componentId: string,
  chosenEvidence: any, 
  validation?: boolean,
  setideaValidationData?: any,
  setMaturity?: any,
  handlebannerText?: any,
  setLoading?: (loading: boolean) => void,
  setValidationCount: any;
  setIsValidationDone: any;
  componentType?: string;
  getUnreadNotificationsCountByRequest?: (requestId: string) => number;
  hoverCard?: boolean;
 }) => {
  const auth0 = useAuth0();
  const currentUser = useCurrentUser();
  const { id: ideaId } = useParams<{ id: string }>();
  const { chooseMatch, 
    setChosenEvidence, closeDrawer } = useEvidenceDrawer();
  const queryClient = useQueryClient();
  const { 
    refetchEvidences,
    refetchMatches,
    idea,
    resolveNextEvidence,
  } = useIdeaPage();
  const { canManageEvidence } = useUserPermissions();
  const { validate, dismiss, isLoading } = useEvidenceActions({ evidenceId: chosenEvidence?._id });
  const { allComponents } = useFields();
  const { defaultStatus , defaultPriority } = useKnowledgeData();

  const isBagelUser = isBagelAdmin(currentUser);
  // const [loadingAssign, setLoadingAssign] = useState(false);
  // const [loadingDismissed, setLoadingDismissed] = useState(false);
  const [labelingDisabaled, setLabelingDisabaled] = useState(
    chosenEvidence?.manualLabel === "sameRequest"
  );
  const [opened, setOpened] = useState(false);
  const [verifyOpened, setVerifyOpened] = useState(false);
  const assignedRequest = (chosenEvidence?.requests && chosenEvidence?.requests?.length > 0)
    ? chosenEvidence?.requests[0]
    : null;
  const assignedIdea = (chosenEvidence?.ideas && chosenEvidence?.ideas?.length > 0)
    ? chosenEvidence?.ideas[0]
    : null;
  const [isBagelUsersActionsOpened, setIsBagelUsersActionsOpened] = useState(false);

  // Helper function to handle navigation to next evidence or close drawer
  const handleNextEvidenceOrClose = useCallback(() => {
    if (hoverCard) {
      return;
    }
    void resolveNextEvidence(chosenEvidence?._id).then((nextEvidence) => {
      if (nextEvidence) {
        chooseMatch(nextEvidence._id);
      } else {
        closeDrawer();
      }
    });
  }, [hoverCard, resolveNextEvidence, chosenEvidence?._id, chooseMatch, closeDrawer]);


  const createNew = () => {
    const newIdeaObj = {
      title: chosenEvidence?.title,
      description: chosenEvidence?.text,
      componentId: componentId,
      ownerId: null,
      rolloutDate: null,
      status: defaultStatus,
      priority: defaultPriority,
    };

    const _body = {
      idea: newIdeaObj,
      evidence: { ...chosenEvidence },
    };

    // Check if a product area has been selected
    if (!componentId) {
      showNotification({
        title: "Product Area Missing",
        color: "red",
        message: "Please assign a producat area to this request before creating a new idea.",
        icon: <IconX size="1.1rem" />,
        autoClose: 1200,
      });
      return;
    }

    // Find the product area by componentId
    const foundComponent = allComponents.find(
      (c) => c._id === componentId
    );

    // If no matching component is found, show an error
    if (!foundComponent) {
      showNotification({
        title: "Invalid Product Area",
        color: "red",
        message: "Please assign a producat area to this request before creating a new idea.",
        icon: <IconX size="1.1rem" />,
        autoClose: 1200,
      });
      return;
    }

    // If a valid product area is found, assign its ownerId to the idea and evidence objects
    _body.idea.ownerId = foundComponent.ownerId;
    _body.evidence.ownerId = foundComponent.ownerId;

    // Submit the new idea request
    assignRequestToNewIdea(_body, auth0)
      .then((response) => {
        const ideaId = response.createNewIdeaRes?.idea?._id;

        showNotification({
          title: "Assigned Successfully!",
          color: "teal",
          message: (
            <Box>
              <Text>New idea has been created.</Text>
              {ideaId && (
                <Anchor href={`/idea/${ideaId}`} target="_blank">
                Go to the new idea
                </Anchor>
              )}
            </Box>
          ),
          icon: <IconCheck size="1.1rem" />,
          autoClose: 1200,
        });

        if (ideaId) {
          refetchMatches()
          setTimeout(() => {
            window.open(`/idea/${ideaId}`);
          }, 1300);
        } else {
          console.log("Did not receive an idea ID for the new idea", response);
        }
      })
      .catch((error) => {
        console.error("Error creating idea:", error);
        showNotification({
          title: "Error Creating Idea",
          color: "red",
          message:
            "An error occurred while creating the idea. Please try again.",
          icon: <IconX size="1.1rem" />,
          autoClose: 1200,
        });
      })
  };

  const onValidateMatch = (matchId: string, ideaId: string, evidence: any ) => {

    if (evidence?.ideaId) {

      

      setVerifyOpened(true);

    } else {
      // Capture the current evidence ID before any state changes
      const currentEvidenceId = chosenEvidence?._id;
      
      // setLoadingAssign(true);
      if(validation){
        setLoading?.(true);
      }
      
      // validateMatch(auth0, matchId, ideaId, evidence, evidence?._id)
      /* const matchesCopy = [...matches]

      const filtered = matchesCopy.filter((match: any)=> match._id !== chosenEvidence?._id)

      setMatches(filtered) */
      validate({ matchId, ideaId, sourceOfAssignment:"suggestion" })
        .then((res) => {
          console.log("validateMatch", { res });
          setChosenEvidence({});
          refetchEvidences()
          refetchMatches();
          showNotification({
            title: "Evidence added successfully",
            color: "teal",
            message: ``,
            icon: <IconCheck size="1.1rem" />,
            autoClose: 1200,
          });
          if (validation) {
            ideaValidation(auth0, idea._id)
              .then((res) => {
                setideaValidationData(res.data);
                setMaturity(res.data.maturityLevel);
                setValidationCount((prevCount:number) => {
                  const newCount = prevCount + 1;
                  const idea_id= idea._id;
                  handlebannerText(newCount);
                  updateValidation(
                    auth0, 
                    {id: idea_id, verifyState:'continue', validationCount: newCount}
                  )
                  if (newCount >= 5) {
                    setIsValidationDone(true); 
                  }
                  return newCount;
                });
                setLoading?.(false);
              })
              .catch((e) => console.log(e));
          }
        })
        .then(() => {
          if(!validation) {
            setChosenEvidence(null);
          }
          // setLoadingAssign(false);
        })
        .catch((e) => {
          showNotification({
            title: "Problem occured!",
            color: "red",
            message: "Problem occured while adding evidence...",
            icon: <IconX size="1.1rem" />,
            autoClose: 1200,
          });
          console.log(e);
        })
        .finally(() => {
          // When assigned from a list row's hover card, no evidence drawer is open,
          // so we must not auto-advance/open the next evidence.
          if (hoverCard) {
            return;
          }
          // Move to next evidence after action is completed, using the captured ID
          void resolveNextEvidence(currentEvidenceId).then((nextEvidence) => {
            if (nextEvidence) {
              chooseMatch(nextEvidence._id);
            } else {
              closeDrawer();
            }
          });
        });
    }
  };

  const dismissMatch = (matchId: string) => {
    // Capture the current evidence ID before any state changes
    const currentEvidenceId = chosenEvidence?._id;
    // setLoadingDismissed(true);
    if(validation){
      setLoading?.(true);
    }

    console.log({ matchId, ideaId , chosenEvidence})
    dismiss({
      ideaId,
      matchId, 
      isGap: true
    })
      .then((success) => {
        if (!success) {
          showNotification({
            title: "Failed to dismiss evidence",
            color: "red",
            message: "Please try again",
            icon: <IconX size="1.1rem" />,
            autoClose: 1200,
          });
          return;
        }
        if(hoverCard){
          setChosenEvidence(null);
        }
        showNotification({
          title: "This evidence has been declined",
          color: "teal",
          message: ``,
          icon: <IconCheck size="1.1rem" />,
          autoClose: 1200,
        });

        if (ideaId && currentEvidenceId) {
          removeEvidenceFromSuggestedInfiniteCache(queryClient, ideaId, currentEvidenceId);
          refetchTabEvidenceTotals(queryClient, ideaId);
        }
        refetchMatches();
        if (validation) {
          ideaValidation(auth0, idea._id)
            .then((res) => {
              setideaValidationData(res.data);
              setMaturity(res.data.maturityLevel);
              setValidationCount((prevCount: number) => {
                const newCount = prevCount + 1;
                const idea_id= idea._id;
                handlebannerText(newCount);
                updateValidation(
                  auth0, 
                  {id: idea_id, verifyState:'continue', validationCount: newCount}
                )
                if (newCount >= 5) {
                  setIsValidationDone(true); 
                }
                return newCount;
              });
              setLoading?.(false);
            })
            .catch((e) => console.log(e));
        }
      })
      .then(() => {
        if(!validation) {
          setChosenEvidence(null);
        }
        // setLoadingDismissed(false);
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        // When dismissed from a list row's hover card, no evidence drawer is open,
        // so we must not auto-advance/open the next evidence.
        if (hoverCard) {
          return;
        }
        // Move to next evidence after action is completed, using the captured ID
        void resolveNextEvidence(currentEvidenceId).then((nextEvidence) => {
          if (nextEvidence) {
            chooseMatch(nextEvidence._id);
          } else {
            closeDrawer();
          }
        });
      });
  };

  const labelMatchBagelUser = (sameRequest: boolean) => {
    // Capture the current evidence ID before any state changes
    const currentEvidenceId = chosenEvidence?._id;
    
    setLabelingDisabaled(true);
    labelMatch(
      chosenEvidence?.match?._id,
      ideaId || "",
      chosenEvidence?._id,
      sameRequest,
      auth0
    )
      .then((res) => {
        console.log(res);
        showNotification({
          title: "This evidence has been added",
          color: "teal",
          message: ``,
          icon: <IconCheck size="1.1rem" />,
          autoClose: 1200,
        });
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        // When acting from a list row's hover card, no evidence drawer is open,
        // so we must not auto-advance/open the next evidence.
        if (hoverCard) {
          return;
        }
        // Move to next evidence after action is completed, using the captured ID
        void resolveNextEvidence(currentEvidenceId).then((nextEvidence) => {
          if (nextEvidence) {
            chooseMatch(nextEvidence._id);
          } else {
            closeDrawer();
          }
        });
      });
  };

  useEffect(() => {
    setLabelingDisabaled(chosenEvidence?.manualLabel === "sameRequest");
  }, [chosenEvidence]);

  if (!canManageEvidence) {
    return (
      <Flex align="center" justify="center" p="sm">
        <Text color="dimmed" size="xs">
          View only - contact Admin or Manager to take action
        </Text>
      </Flex>
    );
  }
  
  return (
    <Flex
      justify={hoverCard ? "flex-start" : "space-between"}
      px={hoverCard ? 0 : "35px"}
      w={hoverCard ? "auto" : "100%"}
      pb={hoverCard ? "-10px" : "20px"} 
      h={hoverCard ? "auto" : "64px"}
      sx={{ borderBottom: hoverCard ? "none" : "1px solid #D8D8DB" }}
      align="center"
    >
      {opened && <Modal
        centered
        opened={opened}
        onClose={() => setOpened(false)}
        size="34vw"
        title={
          <Text fz={20} fw={600}>
            Assign to another idea
          </Text>
        }
      >
        <Box h={440}>
          <AiMatchReAssignment
            idea={idea}
            item={chosenEvidence}
            setOpened={setOpened}
            navToPerspective={navToPerspective}
            TextInputW="100%"
            BoxW="38vw"
          />
        </Box>
      </Modal>}
      {verifyOpened && 
      <AiAssignmentVerificationModal
        verifyOpened={verifyOpened}
        setVerifyOpened={setVerifyOpened}
        navToPerspective={navToPerspective}
        reassignedRequest={assignedRequest}
        chosenEvidence={chosenEvidence}
        validation={validation}
        setideaValidationData={setideaValidationData}
        setMaturity={setMaturity}
        handlebannerText={handlebannerText}
        setLoading={setLoading}
        setValidationCount={setValidationCount}
        setIsValidationDone={setIsValidationDone}
        handleNextEvidenceOrClose={handleNextEvidenceOrClose}
        refetchEvidences={refetchEvidences}
        idea={idea}
      />}

      {!validation && !hoverCard && <AiCommentsMenu 
        chosenEvidence={chosenEvidence}
        // getUnreadNotificationsCountByRequest={getUnreadNotificationsCountByRequest}
      />}
      {isBagelUser && !validation && !hoverCard && (
        <Stack align="center" justify='left' style={{ position: "relative", display: "inline-block" }} mr={2}>
          {/* Target Button */}
          <ActionIcon 
            variant="transparent" 
            onClick={()=> setIsBagelUsersActionsOpened(!isBagelUsersActionsOpened)} 
            style={{ display: "flex", alignItems: "center" }}
          >
            <IconMenu2/>
          </ActionIcon>
  
          {/* Dropdown */}
          {isBagelUsersActionsOpened && (
            <Stack
              justify="center"
              align="center"
              style={{
                position: "absolute",
                bottom: "100%", // Position the dropdown above the button
                /* left: 0, */
                marginBottom: 8, // Spacing between target and dropdown
               
                left: "50%", // Start at the middle of the parent
                transform: "translateX(-50%)", // Adjust to center
                zIndex: 1000,
                background: "white",
                border: "1px solid #ddd",
                borderRadius: 4,
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                padding: "8px 12px",
                /* minWidth: 200, */
              }}
              
            >
              <BagelUsersActions
                labelMatchBagelUser={labelMatchBagelUser}
                labelingDisabaled={labelingDisabaled}
              />
            </Stack>
          )}
        </Stack>
        
      )}
      <Flex
        align="center"
        justify={hoverCard ? "flex-start" : "space-between"}
        w={
          hoverCard
            ? "auto"
            : !validation
              ? undefined
              : "100%"
        }
      >
        {!isLoading.dismiss ? (
          <Tooltip
            label= {validation ? "The product need/gap described in the Evidence is not addressed by this Idea" : "This AI suggestion is not relevant to this Idea. Don’t show it here again"}  
            multiline
            maw={200}
            offset={12}
          >
            <Flex
              className={CLASS_NAMES.notRelevantButton}
              align="center"
              w="107px"
              h="32px"
              mr="12px"
              sx={{ 
                justifyContent: "center",
                backgroundColor: "#5C5CEB1A",
                color: "#5C5CEB",
                cursor: " pointer",
                borderRadius: "4px",
                border:  hoverCard ? "1px solid #5C5CEB" : "none",
                "&:hover": {
                  border: hoverCard ? "1px solid #5C5CEB" : "none",
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                dismissMatch(chosenEvidence?.match?._id);
                setShowHoverCard?.(false);
                posthog.capture(
                  "suggested_not_relevant_clicked",
                  chosenEvidence?.match
                );
              }}
            >
              <Box>
                <IconX size={16} color= "#5C5CEB" style={{marginTop:"5px"}}/>
              </Box>

              <Text ml="4px" color="#5C5CEB" fz={12} lineClamp={1} fw={500}>
                Not relevant
              </Text>
            </Flex>
          </Tooltip>
        ) : (
          <Tooltip
            label= {validation ? "The product need/gap described in the Evidence is not addressed by this Idea" : "This AI suggestion is not relevant to this Idea. Don’t show it here again"}
            multiline
            maw={200}
            offset={12}
          >
            <Flex align="center" w="107px" mr="12px" sx={{ cursor: "default" }}>
              <Box>
                <IconX size={16} color="#D8D8DB" />
              </Box>
              <Text ml="4px" fw={500} color="#D8D8DB" fz={12} lineClamp={1}>
                Not relevant
              </Text>
            </Flex>
          </Tooltip>
        )}
        <Button.Group>
          <Tooltip
            label={"The Evidence described a product need/gap that will be addressed by deploying this Idea"}
            multiline
            maw={200}
            offset={12}
            position="left"
            disabled={!validation}
          >
            <Button
              fz="12px"
              color="indigo"
              w="140px"
              loading={isLoading.validate}
              styles={(theme) => ({
                root: {
                  height: "32px",
                  backgroundColor: "#5C5CEB",
                  border: 0,
                  fontWeight: 400,
                  padding: "0px 5px",
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  "&:hover": {
                    backgroundColor: theme.fn.darken("#5353D3", 0.05),
                  },
                },

                loader: {
                  position: "absolute",
                  color: theme.white,
                },

                leftIcon: {
                  marginRight: 15,
                },
              })}
              onClick={(e) => {
                e.stopPropagation();
                setShowHoverCard?.(false);
                onValidateMatch(
                  chosenEvidence?.match?._id,
                  ideaId || "",
                  chosenEvidence
                );
                posthog.capture("suggested_assign_clicked", chosenEvidence?.match);
              }}
            >
              {!isLoading.validate && (
                <>
                  <IconCircleCheck size={16} fill="white" color="#5C5CEB"/>
                  <Text ml={5} fw={500}>Assign to this idea</Text>
                </>
              )}
            </Button>
          </Tooltip>
          {!validation && 
            <AssignActionsMenu
              actions={[
                {
                  label: "Assign to another idea",
                  onClick: () => {
                    setShowHoverCard?.(false);
                    setOpened(true);
                  },
                },
                {
                  label: "Assign to a new idea",
                  tooltip: "Assign to a new idea",
                  onClick: () => {
                    setShowHoverCard?.(false);
                    createNew();
                  },
                },
              ]}
            />
          }
        </Button.Group>
      </Flex>
    </Flex>
  );
};

export default AiMatchActions;
